import { Link } from 'react-router-dom'
import { Mail, Phone, MapPin } from 'lucide-react'
import { motion, useReducedMotion } from 'framer-motion'
import FitlabLogo from '@/components/brand/FitlabLogo'
import ContactButton, { FITLAB_CONTACT } from '@/components/ui/ContactButton'
import VideoBackground from '@/components/ui/VideoBackground'

/**
 * Footer institucional FITLAB — replica el bloque de cierre del mockup
 * oficial: banda tricolor con tagline "Transformamos datos en decisiones
 * que potencian tu empresa" + CTA Contacto + fila de contacto inferior con
 * email · teléfono · ubicación · copyright.
 */
export default function Footer() {
  const currentYear = new Date().getFullYear()
  const reduced = useReducedMotion()

  return (
    <footer className="relative isolate overflow-hidden">
      {/* Bloque tagline tricolor con CTA — anchor "#contacto" para nav Header */}
      <div
        id="contacto"
        className="relative isolate overflow-hidden bg-[#0b1c3d] py-16 scroll-mt-20"
      >
        {/* Background video — blur 12px + brightness 0.4 + scale 1.2 + rotate.
         * Variantes vs Hero/Process: zoom in + rotación leve, blur fuerte —
         * el video se siente ambient/abstracto, no compite con el tagline. */}
        <VideoBackground
          src="footer"
          className="-z-10"
          blur={12}
          brightness={0.4}
          saturate={1.2}
          scale={1.2}
          rotate={180}
          overlay="linear-gradient(180deg, rgba(11, 28, 61, 0.65) 0%, rgba(11, 28, 61, 0.5) 100%)"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col items-center justify-center gap-6 px-4 text-center sm:px-6 lg:px-8">
          <motion.h2
            initial={reduced ? undefined : { opacity: 0, y: 14 }}
            whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl text-balance text-2xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl"
          >
            Transformamos datos en decisiones que potencian tu empresa
          </motion.h2>
          <motion.div
            initial={reduced ? undefined : { opacity: 0, y: 14 }}
            whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <ContactButton size="lg" channel="whatsapp">
              Contacto
            </ContactButton>
          </motion.div>
        </div>
      </div>

      {/* Fila inferior — contacto + copyright sobre blanco */}
      <div className="bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 py-8 text-sm text-fitlab-neutral-600 sm:flex-row sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center" aria-label="FITLAB inicio">
            <FitlabLogo variant="horizontal" role="img" className="h-9 w-auto" />
          </Link>

          <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <li className="flex items-center gap-1.5">
              <Mail className="h-4 w-4 text-fitlab-blue" strokeWidth={1.75} aria-hidden="true" />
              <a
                href={`mailto:${FITLAB_CONTACT.email}`}
                className="font-medium transition-colors hover:text-fitlab-blue"
              >
                {FITLAB_CONTACT.email}
              </a>
            </li>
            <li className="flex items-center gap-1.5">
              <Phone className="h-4 w-4 text-fitlab-blue" strokeWidth={1.75} aria-hidden="true" />
              <a
                href={`tel:${FITLAB_CONTACT.phoneDigits}`}
                className="font-medium transition-colors hover:text-fitlab-blue"
              >
                {FITLAB_CONTACT.phone}
              </a>
            </li>
            <li className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-fitlab-blue" strokeWidth={1.75} aria-hidden="true" />
              <span className="font-medium">Chile</span>
            </li>
          </ul>

          <p className="text-xs text-fitlab-neutral-600">
            © {currentYear} Fitlab. Todos los derechos reservados.
          </p>
        </div>

        {/* Strip legal final */}
        <div className="border-t border-fitlab-neutral-100 bg-fitlab-neutral-50">
          <div className="mx-auto flex max-w-7xl items-center justify-center gap-6 px-4 py-3 text-xs text-fitlab-neutral-600 sm:px-6 lg:px-8">
            <Link to="/privacidad" className="hover:text-fitlab-blue">
              Política de privacidad
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/terminos" className="hover:text-fitlab-blue">
              Términos de servicio
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
